import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface PayrollRun {
  id: string;
  countryCode: string;
  periodYear: number;
  periodMonth: number;
  runType: 'regular' | 'bonus' | 'off_cycle' | 'correction';
  status: 'draft' | 'calculated' | 'approved' | 'paid' | 'closed';
  payDate: string | null;
  totals: {
    gross_cents?: number;
    net_cents?: number;
    employee_withholding_cents?: number;
    employer_burden_cents?: number;
    total_employer_cash_cents?: number;
    lines_count?: number;
    warnings_count?: number;
  } | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface PayrollLine {
  id: number;
  runId: string;
  employeeId: string;
  countryCode: string;
  currency: string;
  grossCents: number;
  netCents: number;
  employerBurdenCents: number;
  breakdown: Record<string, unknown>;
  healthInsuranceEmployeeCents: number | null;
  healthInsuranceEmployerCents: number | null;
  pensionEmployeeCents: number | null;
  pensionEmployerCents: number | null;
  longTermCareEmployeeCents: number | null;
  longTermCareEmployerCents: number | null;
  employmentInsuranceEmployeeCents: number | null;
  employmentInsuranceEmployerCents: number | null;
  workersCompEmployerCents: number | null;
  incomeTaxCents: number | null;
  residentTaxCents: number | null;
  federalTaxCents: number | null;
  stateTaxCents: number | null;
  ficaEmployeeCents: number | null;
  ficaEmployerCents: number | null;
  niEmployeeCents: number | null;
  niEmployerCents: number | null;
  status: string;
}

export function usePayrollRuns() {
  return useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: async () => {
      const r = await api.get<{ runs: PayrollRun[] }>('/api/payroll/runs');
      return r.data.runs;
    },
  });
}

export function usePayrollRun(id: string | undefined) {
  return useQuery({
    queryKey: ['payroll', 'run', id],
    queryFn: async () => {
      if (!id) return null;
      const r = await api.get<{ run: PayrollRun; lines: PayrollLine[] }>(`/api/payroll/runs/${id}`);
      return r.data;
    },
    enabled: !!id,
  });
}

function invalidatePayroll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['payroll'] });
}

export function useCalculateJpRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      periodYear: number; periodMonth: number; payDate?: string;
      perEmployee?: Record<string, { residentTaxCentsMonthly?: number; dependents?: number; age?: number }>;
    }) => {
      const r = await api.post<{ run: PayrollRun; lines: PayrollLine[]; warnings: string[] }>('/api/payroll/jp/calculate', input);
      return r.data;
    },
    onSuccess: () => invalidatePayroll(qc),
  });
}

export function useCalculateJpBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      periodYear: number; periodMonth: number; payDate?: string;
      perEmployee: Record<string, { bonusCents: number; dependents?: number; previousMonthGrossCents?: number }>;
    }) => {
      const r = await api.post<{ run: PayrollRun; lines: PayrollLine[]; warnings: string[] }>('/api/payroll/jp/bonus', input);
      return r.data;
    },
    onSuccess: () => invalidatePayroll(qc),
  });
}

export function useCalculateUsRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      periodYear: number; periodMonth: number; payDate?: string;
      payPeriodsPerYear: 12 | 24 | 26 | 52;
      perEmployee?: Record<string, { filingStatus?: 'single' | 'married_jointly' | 'head_of_household'; stateCode?: string; allowances?: number }>;
    }) => {
      const r = await api.post<{ run: PayrollRun; lines: PayrollLine[]; warnings: string[] }>('/api/payroll/us/calculate', input);
      return r.data;
    },
    onSuccess: () => invalidatePayroll(qc),
  });
}

export function useCalculateUkRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      periodYear: number; periodMonth: number; payDate?: string;
      payPeriodsPerYear: 12 | 26 | 52;
      perEmployee?: Record<string, { taxCode?: string; pensionContributionPct?: number }>;
    }) => {
      const r = await api.post<{ run: PayrollRun; lines: PayrollLine[]; warnings: string[] }>('/api/payroll/uk/calculate', input);
      return r.data;
    },
    onSuccess: () => invalidatePayroll(qc),
  });
}

export function useCalculateDeRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      periodYear: number; periodMonth: number; payDate?: string;
      payPeriodsPerYear: 12 | 24 | 26 | 52;
      perEmployee?: Record<string, { taxClass?: 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI'; children?: number; churchTax?: boolean; bundesland?: 'BY' | 'BW' | 'OTHER' }>;
    }) => {
      const r = await api.post<{ run: PayrollRun; lines: PayrollLine[]; warnings: string[] }>('/api/payroll/de/calculate', input);
      return r.data;
    },
    onSuccess: () => invalidatePayroll(qc),
  });
}

export function useMarkRunPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      const r = await api.post<{ run: PayrollRun }>(`/api/payroll/runs/${runId}/mark-paid`, {});
      return r.data.run;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function useCloseRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      const r = await api.post<{ run: PayrollRun }>(`/api/payroll/runs/${runId}/close`, {});
      return r.data.run;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

export function payslipPdfUrl(lineId: number): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';
  return `${base}/api/payroll/lines/${lineId}/payslip.pdf`;
}

export interface PayrollSummary {
  year: number;
  month: number | null;
  reportingCurrency: string;
  fxAsOf: string;
  breakdown: Array<{
    currency: string;
    countryCode: string;
    headcount: number;
    rateToReporting: number | null;
    grossInReportingCents: number | null;
    netInReportingCents: number | null;
    employerBurdenInReportingCents: number | null;
  }>;
  totals: {
    headcount: number;
    grossInReportingCents: number;
    netInReportingCents: number;
    employerBurdenInReportingCents: number;
    complete: boolean;
  };
  warnings: string[];
}

export interface AttendanceSummaryRow {
  employeeId: string;
  name: string;
  linked: boolean;
  daysWorked: number;
  workedHours: number;
  overtimeHours: number;
  overtimePayCents: number;
  violation: boolean;
}

/** Pre-run attendance view (worked/overtime per employee for the period) — feeds the run. */
export function usePayrollAttendanceSummary(year: number, month: number) {
  return useQuery({
    queryKey: ['payroll', 'attendance-summary', year, month],
    queryFn: async () => {
      const r = await api.get<{ year: number; month: number; employees: AttendanceSummaryRow[] }>(
        '/api/payroll/attendance-summary', { params: { year, month } },
      );
      return r.data;
    },
  });
}

/** Cross-currency payroll rollup in the company reporting currency (GET /payroll/summary). */
export function usePayrollSummary(year?: number, month?: number) {
  return useQuery({
    queryKey: ['payroll-summary', year ?? 'cur', month ?? 'all'],
    queryFn: async () => {
      const r = await api.get<PayrollSummary>('/api/payroll/summary', { params: { year, month } });
      return r.data;
    },
  });
}
